import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';

interface FAQDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const faqs = [
  {
    question: "How does RepoScan.ai evaluate my GitHub repository so quickly?",
    answer: "Our platform orchestrates **four cutting-edge AI systems** in parallel, analyzing everything from code quality and security posture to community engagement and overall legitimacy. Within **60 seconds**, we consolidate these insights into a single, **multi-model** score and instantly send a comprehensive PDF report to your inbox. Under the hood, advanced concurrency frameworks and high-performance servers make this rapid analysis possible."
  },
  {
    question: "What does the entire analysis flow look like?",
    answer: "The moment you submit a repository URL, our AI engines spin up to dissect the repo's source code, commit history, and community metrics. Meanwhile, our proprietary aggregator (our \"secret sauce\") merges these multi-model outputs. The result is a streamlined verdict—PASS or FAIL—alongside an in-depth explanation of how each component factored into the final rating. We also generate a **PDF report** and prepare a **tweet-friendly summary**, all in under a minute."
  },
  {
    question: "Will there be a public summary of my repo's score?",
    answer: "Absolutely. Alongside your detailed PDF report, we create an optional **concise highlight** that can be shared on social media. This short snippet typically includes your repository link, overall PASS/FAIL status with the total score, and a few standout strengths (e.g., scam safety, code quality). You control how and where you share it!"
  },
  {
    question: "Why is your approach more advanced than other code scanners?",
    answer: "Standard code scanners often focus on static code analysis or basic checks. RepoScan.ai takes it **ten steps further** by deploying **multiple specialized AI models**—each tuned to examine different angles like security vulnerabilities, innovation potential, and community activity. A context-aware aggregator then weighs each engine's output, delivering a truly **holistic** rating that a single-model approach can't match."
  },
  {
    question: "Can I see the full algorithm or code behind RepoScan.ai?",
    answer: "We're transparent about our general framework and scoring criteria, but the mechanics of our internal AI pipelines remain **proprietary**. This preserves the **integrity** of our evaluations, helping to prevent manipulation or reverse-engineering of the scoring system."
  },
  {
    question: "Does a \"PASS\" or \"FAIL\" verdict guarantee reliability or unreliability?",
    answer: "A **PASS** (≥ 6.0) indicates that, according to our metrics, a repository appears to exhibit strong code integrity, transparency, and community engagement. A **FAIL** (< 6.0) suggests potential gaps worth exploring further. We encourage additional due diligence for critical or high-stakes projects regardless of the score."
  },
  {
    question: "How important are stars, forks, and watchers in the final score?",
    answer: "Community engagement, like active stars, forks, and issue discussions, is one of our seven key criteria (worth 15%). However, every category—such as scam safety, code sharing, update frequency—matters. We balance all seven dimensions for a **well-rounded** evaluation."
  },
  {
    question: "How often do you re-check a repository for updates or new commits?",
    answer: "Our system performs **periodic scans** to identify significant changes like recent commits and version releases. When new activity is detected, we refresh the score, send updated results via email, and generate an updated PDF if needed."
  },
  {
    question: "Can I share the PDF report with my team or community?",
    answer: "Certainly! Every time your repo is analyzed, you receive a secure link to your **PDF report** at pdf.reposcan.ai. Feel free to share it anywhere you want—social channels, email, or other platforms."
  },
  {
    question: "Are you affiliated with GitHub?",
    answer: "No, we're an **independent platform** leveraging publicly available GitHub APIs (and other data) to power our real-time, AI-driven analyses. Our mission is to provide an **unbiased**, **multi-perspective** evaluation that fosters trust and transparency in open-source software."
  }
];

export function FAQDialog({ isOpen, onClose }: FAQDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] p-0 bg-background-primary border-border">
        <DialogTitle className="p-6 text-xl font-semibold text-text-primary border-b border-border">
          Frequently Asked Questions
        </DialogTitle>
        
        <ScrollArea className="h-[70vh] p-6">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-text-primary hover:text-text-primary hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-text-secondary [&_strong]:text-text-primary [&_strong]:font-semibold">
                  {faq.answer.split(/(\*\*.*?\*\*)/).map((part, i) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                      return <strong key={i}>{part.slice(2, -2)}</strong>;
                    }
                    return part;
                  })}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}